'use client'

import { Spinner } from '@/components/ui/spinner'
import {
  useCreateBusinessProfileMutation,
  useDeleteBusinessProfileMutation,
  useUpdateBusinessProfileMutation,
} from '@/lib/features/businessProfile'
import { BusinessProfile } from '@/lib/features/businessProfile/types'
import { cn } from '@/lib/utils'
import { AnimatePresence, motion } from 'framer-motion'
import { AlertTriangle, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { ImageUpload } from './ImageUpload'

interface BusinessProfileModalProps {
  isOpen: boolean
  onClose: () => void
  profile?: BusinessProfile | null
  mode: 'create' | 'edit' | 'delete'
}

export function BusinessProfileModal({
  isOpen,
  onClose,
  profile,
  mode,
}: BusinessProfileModalProps) {
  const [createProfile, { isLoading: isCreating }] = useCreateBusinessProfileMutation()
  const [updateProfile, { isLoading: isUpdating }] = useUpdateBusinessProfileMutation()
  const [deleteProfile, { isLoading: isDeleting }] = useDeleteBusinessProfileMutation()

  const [formData, setFormData] = useState({
    name: '',
    landingPageUrl: '',
    logoImageUrl: '',
    privacyPolicyUrl: '',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  // Initialize form data when profile or mode changes
  useEffect(() => {
    if (mode === 'edit' && profile) {
      setFormData({
        name: profile.name || '',
        landingPageUrl: profile.landingPageUrl || '',
        logoImageUrl: profile.logoImageUrl || '',
        privacyPolicyUrl: profile.privacyPolicyUrl || '',
      })
    } else if (mode === 'create') {
      setFormData({
        name: '',
        landingPageUrl: '',
        logoImageUrl: '',
        privacyPolicyUrl: '',
      })
    }
    setErrors({})
  }, [profile, mode, isOpen])

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Profile name is required'
    } else if (formData.name.length > 100) {
      newErrors.name = 'Profile name must be 100 characters or less'
    }

    if (formData.landingPageUrl && !isValidUrl(formData.landingPageUrl)) {
      newErrors.landingPageUrl = 'Please enter a valid URL'
    }

    if (formData.privacyPolicyUrl && !isValidUrl(formData.privacyPolicyUrl)) {
      newErrors.privacyPolicyUrl = 'Please enter a valid URL'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const isValidUrl = (string: string) => {
    try {
      new URL(string)
      return true
    } catch {
      return false
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    try {
      if (mode === 'create') {
        await createProfile({
          name: formData.name,
          landingPageUrl: formData.landingPageUrl || null,
          logoImageUrl: formData.logoImageUrl || null,
          privacyPolicyUrl: formData.privacyPolicyUrl || null,
        }).unwrap()
        toast.success('Business profile created successfully')
      } else if (mode === 'edit' && profile) {
        await updateProfile({
          id: profile.id,
          data: {
            name: formData.name,
            landingPageUrl: formData.landingPageUrl || null,
            logoImageUrl: formData.logoImageUrl || null,
            privacyPolicyUrl: formData.privacyPolicyUrl || null,
          },
        }).unwrap()
        toast.success('Business profile updated successfully')
      }
      onClose()
    } catch (error: any) {
      const message = error?.data?.error || 'An error occurred'
      toast.error(message)
    }
  }

  const handleDelete = async () => {
    if (!profile) return

    try {
      await deleteProfile(profile.id).unwrap()
      toast.success('Business profile deleted successfully')
      onClose()
    } catch (error: any) {
      const message = error?.data?.error || 'Failed to delete profile'
      toast.error(message)
    }
  }

  const isLoading = isCreating || isUpdating || isDeleting

  const renderDeleteConfirmation = () => (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100">
          <AlertTriangle className="h-4 w-4 text-red-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-black">Delete Business Profile</h3>
          <p className="mt-1 text-sm text-black/60">
            Are you sure you want to delete &ldquo;{profile?.name}&rdquo;? This action cannot be
            undone.
          </p>
          <div className="mt-4 rounded-lg border border-orange-200 bg-orange-50 p-4">
            <p className="text-sm text-orange-800">
              <strong>Warning:</strong> This will permanently delete all associated credentials and
              may affect existing copilot sessions.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6 flex gap-3">
        <button
          type="button"
          onClick={onClose}
          disabled={isLoading}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl border border-[#E0E0E0] bg-white px-4 py-3 text-base font-medium text-black hover:bg-gray-50 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        >
          Cancel
        </button>
        <div
          className={cn(
            'flex-1 rounded-[18px] p-[2px] transition-all duration-300',
            isLoading ? 'bg-gray-300' : 'bg-gradient-to-r from-red-300 to-red-500'
          )}
        >
          <button
            type="button"
            onClick={handleDelete}
            disabled={isLoading}
            className={cn(
              'inline-flex w-full items-center justify-center gap-2',
              'px-4 py-3 text-base font-medium shadow-sm',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
              'rounded-[16px] transition-all duration-200',
              isLoading
                ? 'cursor-not-allowed bg-gray-100 text-gray-400 focus-visible:ring-gray-300'
                : 'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-300 active:scale-95'
            )}
          >
            {isLoading ? <Spinner size={16} /> : null}
            <span>{isDeleting ? 'Deleting...' : 'Delete Profile'}</span>
          </button>
        </div>
      </div>
    </div>
  )

  const renderForm = () => (
    <form onSubmit={handleSubmit}>
      <div className="space-y-4">
        {/* Profile Name */}
        <div>
          <label htmlFor="name" className="mb-1 block text-sm font-medium text-black">
            Profile Name *
          </label>
          <p className="mb-2 text-sm text-black/60">A unique name for this business profile</p>
          <input
            id="name"
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="e.g., Main Profile, Acme Corp"
            className={cn(
              'w-full rounded-2xl border bg-[#F3F3F3] p-4 text-base text-black placeholder:text-gray-400 focus:outline-none',
              errors.name ? 'border-red-500' : 'border-[#E0E0E0] focus:border-[#B0B0B0]'
            )}
            maxLength={100}
          />
          {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
        </div>

        {/* Landing Page URL */}
        <div>
          <label htmlFor="landingPageUrl" className="mb-1 block text-sm font-medium text-black">
            Landing Page URL
          </label>
          <p className="mb-2 text-sm text-black/60">Your business website or landing page</p>
          <input
            id="landingPageUrl"
            type="url"
            value={formData.landingPageUrl}
            onChange={(e) => handleInputChange('landingPageUrl', e.target.value)}
            placeholder="https://example.com"
            className={cn(
              'w-full rounded-2xl border bg-[#F3F3F3] p-4 text-base text-black placeholder:text-gray-400 focus:outline-none',
              errors.landingPageUrl ? 'border-red-500' : 'border-[#E0E0E0] focus:border-[#B0B0B0]'
            )}
          />
          {errors.landingPageUrl && (
            <p className="mt-1 text-xs text-red-600">{errors.landingPageUrl}</p>
          )}
        </div>

        {/* Logo Image */}
        <div>
          <label className="mb-1 block text-sm font-medium text-black">Logo Image</label>
          <p className="mb-2 text-sm text-black/60">Upload your business logo (optional)</p>
          <ImageUpload
            value={formData.logoImageUrl}
            onChange={(url) => handleInputChange('logoImageUrl', url)}
            onError={(error) => setErrors((prev) => ({ ...prev, logoImageUrl: error }))}
            placeholder="Upload logo"
          />
          {errors.logoImageUrl && (
            <p className="mt-1 text-xs text-red-600">{errors.logoImageUrl}</p>
          )}
        </div>

        {/* Privacy Policy URL */}
        <div>
          <label htmlFor="privacyPolicyUrl" className="mb-1 block text-sm font-medium text-black">
            Privacy Policy URL
          </label>
          <p className="mb-2 text-sm text-black/60">Link to your privacy policy page</p>
          <input
            id="privacyPolicyUrl"
            type="url"
            value={formData.privacyPolicyUrl}
            onChange={(e) => handleInputChange('privacyPolicyUrl', e.target.value)}
            placeholder="https://example.com/privacy"
            className={cn(
              'w-full rounded-2xl border bg-[#F3F3F3] p-4 text-base text-black placeholder:text-gray-400 focus:outline-none',
              errors.privacyPolicyUrl ? 'border-red-500' : 'border-[#E0E0E0] focus:border-[#B0B0B0]'
            )}
          />
          {errors.privacyPolicyUrl && (
            <p className="mt-1 text-xs text-red-600">{errors.privacyPolicyUrl}</p>
          )}
        </div>
      </div>

      <div className="mt-6 flex gap-3">
        <button
          type="button"
          onClick={onClose}
          disabled={isLoading}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl border border-[#E0E0E0] bg-white px-4 py-3 text-base font-medium text-black hover:bg-gray-50 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        >
          Cancel
        </button>
        <div
          className={cn(
            'flex-1 rounded-[18px] p-[2px] transition-all duration-300',
            isLoading ? 'bg-gray-300' : 'bg-gradient-to-r from-pink-300 to-cyan-300'
          )}
        >
          <button
            type="submit"
            disabled={isLoading}
            className={cn(
              'inline-flex w-full items-center justify-center gap-2',
              'px-4 py-3 text-base font-medium shadow-sm',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
              'rounded-[16px] transition-all duration-200',
              isLoading
                ? 'cursor-not-allowed bg-gray-100 text-gray-400 focus-visible:ring-gray-300'
                : 'bg-[#3f3f46] text-white hover:bg-[#2f2f34] focus-visible:ring-[#6b7280] active:scale-95'
            )}
          >
            {isLoading ? <Spinner size={16} /> : null}
            <span>
              {isLoading
                ? mode === 'create'
                  ? 'Creating...'
                  : 'Saving...'
                : mode === 'create'
                  ? 'Create Profile'
                  : 'Save Changes'}
            </span>
          </button>
        </div>
      </div>
    </form>
  )

  const getTitle = () => {
    switch (mode) {
      case 'create':
        return 'Create Business Profile'
      case 'edit':
        return 'Edit Business Profile'
      case 'delete':
        return 'Delete Business Profile'
      default:
        return 'Business Profile'
    }
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="font-hanken-grotesk flex h-full max-h-[90vh] w-full max-w-lg flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex h-full min-h-0 flex-col rounded-3xl bg-white p-8 text-black shadow-xl">
            {/* Header */}
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-black">{getTitle()}</h2>
                <p className="mt-1 text-base text-black/70">
                  {mode === 'create'
                    ? 'Set up a new business profile with your branding and information'
                    : mode === 'edit'
                      ? 'Update your business profile information'
                      : 'Permanently remove this business profile'}
                </p>
              </div>
              <button
                onClick={onClose}
                className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="min-h-0 flex-1 overflow-y-auto">
              {mode === 'delete' ? renderDeleteConfirmation() : renderForm()}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
